import { useRef, useEffect } from 'react'
import { createFootprintCellGL, isFootprintCellGLEnabled } from '../utils/footprintCellGL'
import { requestFrame, cancelFrame, frameNow, FRAME_LAYER } from '../utils/frameDriver'
import { setCtxFont } from '../utils/canvasFont'
import { dbg } from '../utils/debugFlag'

/**
 * LiquidityHeatLayer — the EXTENDED-AREA book flow, synced to OrderflowTrail.
 *
 * The slot-quantized heat under the footprint stays entirely in the main
 * canvas (unchanged). This layer draws only the region PAST the live edge:
 * recent book states streaming at the TRAIL'S exact speed on the
 * trail's exact ruler — same x for the same instant, whole lifetime.
 * The "Extend fwd" knob sets the drop-off in seconds, capped at the
 * trail's window (knob at cap = flow spans the trail's full length).
 * flowDir mirrors the trail's toggle.
 * This REPLACES the old "Extend fwd" projection: instead of repeating the
 * current book rightward, the extended area shows the book's actual recent
 * evolution, newest state pinned at the SEAM — the forming column's edge,
 * where the main canvas's slot heat ends. The seam x comes from getSeamX,
 * which the mount builds from the canvas heat pass's VERBATIM formulas;
 * a re-derived origin was one clamp different and the error scaled with
 * column width (overlap zoomed out, gap zoomed in). Dots still launch at
 * candle center, which reads fine because dots sit ON heat anyway.
 *
 * Follows OrderflowTrail's conventions exactly (the hard-won ones):
 *  - Own rAF loop, divisor-paced to HEAT_FPS, paused while the tab hides.
 *  - Atomic per-frame geometry via getGeom() — rowHeight + pinPx read
 *    together so a rebucket can't mix lattices; seam x via getSeamX().
 *    Heat is PINNED (like the canvas): pinPx never includes scrollTop.
 *  - LTR wrapper escaping the RTL scaffold; parent overflow clips at the
 *    y-axis; zIndex -1 (under vol-profile z0, trail z1, footprint z2+).
 *
 * DATA: one book object from utils/hlBookFeed (see its header). The
 * layer is a pure consumer: book.flow in, ribbons out.
 *
 * NORMALIZATION: heatNormRef {lo, span} — p25/p97 of drawn row totals in
 * log space, EMA-smoothed. THIS LAYER IS THE WRITER: the percentile fold
 * at the end of the history pass. FPCC's heat pass held that job until it
 * was ported into this file and cut (see its "Heat render: MOVED to
 * LiquidityHeatLayer" note). Same lo/hi slider refs and the same 33-bucket
 * LUT on both sides, so brightness stays continuous where the flow meets
 * the forming column's slot heat at the live edge.
 * The fold sits INSIDE the history block, so anything that skips that block
 * leaves the window frozen at whatever it last held.
 *
 * FLOW DIRECTION: follows the trail's flowDir. 'right' (the intended use)
 * streams into the extended/future region. 'left' would stream over the
 * chart body ON TOP of the slot heat — accepted for now so the two layers
 * never disagree about direction; revisit if left-mode doubling reads
 * badly in practice.
 */
/**
 * Frame cap for the IDLE loop — back to the trail's 30. The earlier bump
 * to 60 was treating a symptom: interaction sync is not a polling problem,
 * it is an ORDERING problem (the layer's rAF vs the zoom animation's rAF),
 * and it is solved by paintHookRef — the canvas invokes our paint at the
 * tail of every full draw with the refs it just used. The loop only has
 * to carry the idle flow, which moves at a column per knob-seconds ≈
 * hundredths of a px per ms; 30fps is generous for that.
 */
const HEAT_FPS = 30
// The trail's speed basis: its pxPerMs = width / TRAIL_WINDOW_MS. Bands'
// ENTRY speed is pinned to this (see the mapping note), so this constant
// MUST equal OrderflowTrail's windowMs or the co-location at the seam lies.
const TRAIL_WINDOW_MS = 30000

// ── window.__cheddarHeatGL — NON-DESTRUCTIVE GL READOUT ──────────────────
// There is no safe way to ASK a canvas which context it holds: getContext()
// is create-if-absent, so probing for '2d' on a canvas that has not painted
// yet CREATES a 2d context and permanently locks WebGL out of that element.
// This reports the answer from the inside instead.
//
//   enabled  isFootprintCellGLEnabled() — the localStorage kill switch
//   mode     'scene' | 'own' | '2d'  — which path actually ran this paint
//   ok/lost  the controller's own flags (lost is set by webglcontextlost and
//            is permanent by design; the chart stays 2D until remount)
//   colors   distinct fill colours submitted — this is the draw-call count,
//            since the solid path issues one drawArrays per colour
//   rects    quads submitted this paint
//   hist     did the history pass re-WALK this frame (own-GL replays its
//            cached batches on clean frames, so false there is normal)
//   flowN    flow entries walked
//   paints   monotonic counter; diff it over a second for the paint rate
const HEAT_PROBE = {
  enabled: false, mode: '2d', ok: false, lost: false,
  colors: 0, rects: 0, hist: false, flowN: 0, paints: 0, ts: 0,
}
if (typeof window !== 'undefined') window.__cheddarHeatGL = HEAT_PROBE
// (Warm-up outline deleted with the budgeted warm-up it marked: every entry
// now projects in the frame it is first needed, so there is no fill front
// to outline. It had also been firing on every 2Hz append frame — the
// newest entry is cold until painted — outlining short segments in steady
// state, the opposite of "nothing is outlined once warm".)
// Vertical overdraw (px each side) so translateY(REG) never exposes blank
// edges. Covers the sub-row residual plus a healthy live-drag range; an
// extreme drag beyond this clips again, visibly but harmlessly.
const HEAT_OVERDRAW = 96
// Forming CB column aggregates this far back — the window the 5m chart
// already had (FPCC's HEAT_LIVE_MS, ported with the CB pass).
const CB_LIVE_MS = 300_000
// The merge flag (cheddar_heat_in_layer) is GONE: this layer is the only
// heat renderer — FPCC's ~470-line block was ported here, verified on live
// tape behind the flag, then deleted. One canvas, two regions: history
// [0, histEdge] on its own dirty key, flow band (histEdge, seam] on the
// pxPerMs blit.

export default function LiquidityHeatLayer({
  book,              // hlBookFeed's book object: this layer reads .flow (the
                     // ingest-maintained deque of compact running-book states)
                     // and .gen (reset detection for the blit). The FOLD that
                     // used to live here — cursor, window-replace, trim
                     // relocation, retention — is deleted whole: flow
                     // maintenance moved to the module's ingest, so this
                     // layer needs zero fold calls. Cache annotations it
                     // writes on flow entries (st.rr / st.rt / st.rk) are
                     // documented as layer-owned in the module header.
  cbSnapsRef,        // Coinbase snapshot ring (FPCC's CB WS effect writes it).
  cbMaxSzRef,        // CB session max — CB stays MAX-normalized on purpose:
                     // HL perp sizes dwarf CB spot top-20, so a shared window
                     // squashed every amber level invisible. Independent
                     // brightness scales by design.
  normRef,           // heatNormRef — {lo, span}: EMA-smoothed p25/p97 of drawn
                     // row totals (log space). WRITTEN HERE — the percentile
                     // fold in the history pass is the single writer, since
                     // FPCC's heat pass was ported out and cut. Everything
                     // else reads it and maps through the same window, which
                     // is what keeps seam brightness continuous
  loRef, hiRef,      // heatLoRef / heatHiRef — sensitivity window
  rampRef,
  gammaRef,          // heatGammaRef  - color curve exponent (1 = linear)           // heatRampRef   — 7 hex stops
  showValuesRef,     // optional — when true, publish size labels for FPCC 2D
  labelsOutRef,      // optional ref — FPCC draws these on the cleared main canvas
  priceToY,          // continuous Y (row-EDGE: key at row bottom). Rows do NOT come from this:
                     // they come from getGeom().rowOfPrice, the chart's own
                     // membership, so heat rows are the cells' rows. This is
                     // only the fallback before the chart's first paint.
  getGeom,           // () => { rowHeight, pinPx, cw } — atomic single read;
                     // pinPx = pinned translate (heat is pinned; = lastStyleRef.pinPx).
                     // regionPx may still appear as a transitional alias of pinPx.
                     // cw sizes the one-column flow strip (see mapping note)
  extendRef,         // heatExtendRef — drop-off in SECONDS, capped at the
                     // trail's 30s window (0 = off);
                     // the Heatmap menu's "Extend fwd" input. NOTE: the CB
                     // pass's static projection reads the same knob as
                     // COLUMNS — one control, two units, flagged in the
                     // tooltip; a separate CB knob is the clean fix if it
                     // ever grates
  getSeamX,          // () => the forming column's OUTER edge x, computed with
                     // the CANVAS heat pass's verbatim formulas (its endIdx/
                     // startIdx clamps, its plot floor, its dpr-quantized pan)
                     // — the seam is the canvas's line, not a re-derivation
  paintHookRef,      // FPCC assigns .current = our paint; the canvas calls it
                     // at the tail of every full draw so interaction repaints
                     // happen same-frame, deterministic order (zoom sync)
  interactingRef,    // optional ref<{current:boolean}> — while true, skip full
  hideWhileRef,      // optional — opacity 0 while true (x-pan drag only; wheel stays visible)
                     // fills and only keep translateY(REG) in sync (pan/zoom freeze)
  sceneGlRef,        // unused by live mount (own-canvas SoT); kept optional for dormant shared-scene branch
  flowDir = 'right', // MUST mirror the trail's flowDir for the sync to hold
  width,             // px — chart area width (liveEdge); parent overflow clips
  height,
  active = true,
}) {
  const canvasRef = useRef(null)
  const glCanvasRef = useRef(null)
  const heatValueOverlayRef = useRef(null)  // CSS-sized canvas for FBO label stamp
  // controller | false (create failed) | null (not tried) — shared kill switch w/ footprint
  const glRef = useRef(null)
  const rafRef = useRef(0)
  // LUT cache: rebuilt only when the inputs that shape it change.
  const lutRef = useRef({ key: '', lut: [] })
  // Last REG written to the canvas element's transform (write-on-change,
  // same discipline as FPCC's lastStyleRef — identical transforms every
  // frame force needless compositor work).
  const lastRegRef = useRef(NaN)
  // Geometry key of the last hook-driven paint (see hookPaint).
  const lastGeomKeyRef = useRef('')
  // True after a freeze-frame early-return; next non-interacting paint must
  // full-redraw (clear geom/blit keys) so hookPaint doesn't skip catch-up.
  const wasInteractingRef = useRef(false)
  // ── Scroll-blit state (see the blit note in paintNow) ──
  // Ribbons made per-paint fill AREA the cost (true-length rects × rows,
  // full repaint each frame). But ribbon history is IMMUTABLE — painted
  // past only TRANSLATES (time drift + pan are both pure x-shifts). So:
  // shift the existing bitmap by integer DEVICE pixels per frame, carry
  // the sub-pixel remainder on the element transform (which already
  // carries REG), and repaint only the vacated sliver at the seam. Full
  // redraw only when the mapping itself changes (zoom/RH/ramp/knob/
  // resize/reset) or on probe frames.
  //   key      : full-redraw fingerprint
  //   lastNow  : arrival-clock time of the last paint
  //   lastSeam : seamX at the last paint
  //   xRem     : carried sub-pixel x remainder (element transform)
  //   histKey  : history region's own dirty key
  //   hnLo/hnSpan : the normalizer the history pixels were PAINTED with.
  //     Not part of histKey — see the dirty-key note at the histKey site;
  //     this pass writes normRef, so keying on it exactly meant the key
  //     could never report clean. Compared against a bucket-width
  //     tolerance instead. NaN = never painted ⇒ dirty.
  const blitRef = useRef({ key: '', lastNow: 0, lastSeam: NaN, xRem: 0, histKey: '',
                           hnLo: NaN, hnSpan: NaN, p25: NaN, p97: NaN, hnTs: NaN })
  // Percentile scratch — reused, never reallocated (per-frame garbage here
  // was already profiled once as a Major-GC source).
  const hnScratchRef = useRef(new Float64Array(4096))   // histKey: history region's own dirty key (step 3)

  const stateRef = useRef({})
  stateRef.current = { priceToY, getGeom, getSeamX, flowDir, width, height }
  const sceneGlRefMirror = useRef(sceneGlRef)
  sceneGlRefMirror.current = sceneGlRef

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const glCanvas = glCanvasRef.current

    // ── Divisor pacing, verbatim from OrderflowTrail ──
    const FRAME_MS = 1000 / HEAT_FPS
    let prevTs = 0
    let emaDelta = 1000 / 60
    let frameCount = 0

    const hexRgb = (h) => {
      const v = parseInt((h || '').replace('#', ''), 16)
      return Number.isFinite(v) ? [(v >> 16) & 255, (v >> 8) & 255, v & 255] : [40, 100, 255]
    }
    const RAMP_POS = [0.00, 0.30, 0.55, 0.72, 0.82, 0.91, 1.00]

    // ── EFFECT-SCOPE SCRATCH (reused every paint, never per-frame garbage) ──
    // Colour batches: the Maps are cleared after each submit, and each
    // colour's array is pooled and truncated rather than reallocated, so a
    // steady-state frame allocates no batch storage at all. drawColorBatches
    // copies into its own vertex store synchronously, so reuse is safe.
    const fillBatch = new Map()
    const fillBatch055 = new Map()
    const _pool = new Map(), _pool055 = new Map()
    // Row projection scratch (see projectEntry): rows per price, then a
    // dense per-row accumulator over the entry's row span.
    let _rowScr = new Float64Array(1024)
    let _accScr = new Float64Array(1024)
    let _hitScr = new Uint8Array(1024)
    // Open flow runs, reused (cleared per paint).
    const open = new Map()   // r -> { x0, q, tot, g }
    // Last size handed to the own-GL controller (chart-perf): skip unchanged resizes.
    const glSizeMemo = { gl: null, w: 0, h: 0, dpr: 0 }

    let lastPaintTs = 0
    const paintNow = (opts) => {
      lastPaintTs = performance.now()
      const s = stateRef.current
      const bk = book
      if (!bk) return
      const glSubmit = opts && (opts.mode === 'gl-submit' || opts.mode === 'force')

      // Geom first — freeze path needs REG before any clear/GL/fill work.
      const geom = (typeof s.getGeom === 'function') ? s.getGeom() : null
      if (!geom) return
      // Heat is PINNED — pinPx from lastStyleRef via getGeom (fallback regionPx
      // only during transition). Never add scrollTop here.
      const REG = Number.isFinite(geom.pinPx) ? geom.pinPx : geom.regionPx

      // ── Pan/zoom freeze: CSS translateY only (no fills / blit / GL) ──
      // Own-canvas SoT — FPCC does not blit heat. force/gl-submit always paints.
      if (interactingRef?.current && !glSubmit) {
        wasInteractingRef.current = true
        // Opacity hide while hideWhileRef (same gate as FPCC skip-blit).
        const hide = !!hideWhileRef?.current
        if (hide) {
          if (canvas.style.opacity !== '0') canvas.style.opacity = '0'
          if (glCanvas && glCanvas.style.opacity !== '0') glCanvas.style.opacity = '0'
        } else {
          if (canvas.style.opacity !== '1') canvas.style.opacity = '1'
          if (glCanvas && glCanvas.style.opacity !== '1') glCanvas.style.opacity = '1'
        }
        const _tf = `translateY(${REG}px)`
        if (_tf !== lastRegRef.current) {
          canvas.style.transform = _tf
          lastRegRef.current = _tf
        }
        if (glCanvas) glCanvas.style.transform = lastRegRef.current || _tf
        return
      }
      if (wasInteractingRef.current) {
        wasInteractingRef.current = false
        lastGeomKeyRef.current = ''
        blitRef.current.key = ''
        if (canvas.style.opacity !== '1') canvas.style.opacity = '1'
        if (glCanvas && glCanvas.style.opacity !== '1') glCanvas.style.opacity = '1'
      }

      // ONE CLOCK FOR THE FRAME. Was Date.now() sampled here, which meant
      // this layer and OrderflowTrail positioned the same frame from two
      // different instants — both animate on elapsed time, so the gap
      // between their callbacks WAS drift between the ribbon and the
      // trail. frameNow() is the presentation time of the frame being
      // dispatched, identical for every callback in it, sub-millisecond,
      // monotonic, and still Date.now()-comparable so it can be
      // subtracted from feed timestamps unchanged.
      const now = frameNow()

      // ── Canvas + geometry (atomic) ──
      // dpr FIRST — the canvas sizing block below uses it, and the
      // device-grid quantization further down uses it too. Declaring it
      // beside its second use put every frame in the TDZ (esbuild passes;
      // only runtime shows it) — the scope-audit rule exists for exactly
      // this and I skipped it.
      const dpr = window.devicePixelRatio || 1
      const w = Math.max(1, s.width)
      const h = Math.max(1, s.height)
      // OVERDRAW MARGIN: the element rides translateY(REG); with a bitmap
      // exactly viewport-height, that shift exposed blank at one edge and
      // clipped rows at the other — edge rows popped as REG moved. The
      // main canvas never has this problem because its bitmap spans ALL
      // rows. Same cure: bitmap taller by HEAT_OVERDRAW each side, element
      // top offset by -HEAT_OVERDRAW, row coordinates unchanged (baked
      // into the transform's y term).
      const bh = h + 2 * HEAT_OVERDRAW
      // ROUND BEFORE COMPARING. canvas.width is an INTEGER — the DOM coerces
      // whatever you assign — while w comes from `liveEdge`
      // (containerWidth - RIGHT_PADDING - axisW) and is routinely fractional.
      // So `canvas.width !== w * dpr` compared an integer against a fraction
      // and was ALWAYS TRUE: the bitmap was reassigned on EVERY paint.
      //
      // That is not a cheap no-op. Assigning canvas.width reallocates the
      // drawing buffer, implicitly clears it, and makes the compositor rebuild
      // the layer — which is why Layerize climbed back to 24.3% of the trace
      // after it had been driven down to 8.5%, and why footprintCellGL's
      // resize() still showed 234ms with its FBO retry loop already fixed.
      //
      // _resized also feeds histDirty, so history was being force-repainted
      // every frame for a resize that was not happening.
      const cvW = Math.max(1, Math.round(w * dpr))
      const cvH = Math.max(1, Math.round(bh * dpr))
      let _resized = false
      if (canvas.width !== cvW || canvas.height !== cvH) {
        canvas.width = cvW            // implicit clear; forces the full path
        canvas.height = cvH
        canvas.style.height = `${bh}px`
        _resized = true
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, dpr * HEAT_OVERDRAW)
      // NO unconditional clear — the blit path preserves painted history.
      // Clearing is owned by whichever path runs (full clears all,
      // incremental clears only the strips it repaints).

      // ── WebGL heat fills ──
      // Prefer shared chart scene (FPCC cell GL canvas). Else own heat GL canvas.
      const _sceneRef = sceneGlRefMirror.current
      const scene = (_sceneRef && _sceneRef.current && _sceneRef.current.ok)
        ? _sceneRef.current : null
      if (!scene && isFootprintCellGLEnabled() && glCanvas && glRef.current === null) {
        glRef.current = createFootprintCellGL(glCanvas) || false
      }
      const ownGl = (!scene && glRef.current && glRef.current.ok) ? glRef.current : null
      const gl = scene || ownGl
      let useGL = !!gl
      const useScene = !!scene
      // Shared scene canvas has no HEAT_OVERDRAW top offset — use raw chart Y.
      // Own heat GL canvas is top:-OVERDRAW, so bias +OVERDRAW like before.
      const yBias = useScene ? 0 : HEAT_OVERDRAW
      // color -> flat [x,y,w,h,...]; y already biased for GL (see pushHeatFill)
      // Effect-scope Maps (above); cleared here too so a frame that threw
      // mid-walk cannot leak its rects into this one.
      fillBatch.clear(); fillBatch055.clear()
      const pushHeatFill = (color, x, y, rw, rh, batch = fillBatch) => {
        if (useGL) {
          let q = batch.get(color)
          if (!q) {
            const pool = batch === fillBatch ? _pool : (batch === fillBatch055 ? _pool055 : null)
            q = pool ? pool.get(color) : null
            if (q) q.length = 0
            else { q = []; if (pool) pool.set(color, q) }
            batch.set(color, q)
          }
          q.push(x, y + yBias, rw, rh)
        } else {
          ctx.fillStyle = color
          ctx.fillRect(x, y, rw, rh)
        }
      }
      // ── GL submit WITH a 2D fallback ──────────────────────────────────
      // drawColorBatches returns false when the draw did not happen
      // (context lost, buffer alloc fail). FPCC's cell path has always
      // honoured that return — false there falls through to 2D fills. This
      // layer discarded it, and because the GL path fully clears the 2D
      // canvas before submitting (see the clearRect right after
      // beginHeatLayer), a false return rendered NOTHING: cleared 2D over
      // empty GL, no error thrown, no recovery, no way to tell it from a
      // genuinely empty book. Replay the batch onto 2D instead.
      //
      // − yBias: batched coords carry +yBias because GL has no transform,
      // while this ctx already applies dpr*HEAT_OVERDRAW in its
      // setTransform. The bias has to come back OFF or every fallback fill
      // lands a full overdraw margin low.
      //
      // destination-over: the GL canvas is zIndex 0, UNDER this 2D canvas
      // (zIndex 1), and the warm-up outlines were already stroked here
      // during the walk. Painting underneath reproduces that stacking
      // instead of burying them. The two batches cannot overlap each other
      // — the history/CB walk covers real columns (i <= cols.total - 1) and
      // the 055 projection only draws past them — so their order under
      // destination-over does not matter.
      //
      // save/restore is not optional: a leaked globalCompositeOperation or
      // globalAlpha would poison every later frame on this context, which is
      // the same failure shape as a leaked clip.
      //
      // CALLERS MUST NOT BE INSIDE A CLIP. GL ignores 2D clips, so the
      // batched fills have always drawn unclipped; a fallback that inherited
      // the flow strip's clip would silently drop every history column.
      // Probe counts, summed over the COLOUR map rather than the rects, so
      // this is O(colors) and costs nothing at scale.
      const probeCount = (batch) => {
        HEAT_PROBE.colors += batch.size
        for (const [, q] of batch) HEAT_PROBE.rects += (q.length / 4) | 0
      }
      const drawBatchOr2D = (batch, alpha) => {
        if (!batch.size) return
        probeCount(batch)
        if (gl.drawColorBatches(batch, alpha)) return
        ctx.save()
        ctx.globalCompositeOperation = 'destination-over'
        ctx.globalAlpha = alpha
        for (const [color, q] of batch) {
          ctx.fillStyle = color
          for (let i = 0; i < q.length; i += 4) {
            ctx.fillRect(q[i], q[i + 1] - yBias, q[i + 2], q[i + 3])
          }
        }
        ctx.restore()
      }
      // Optional size labels (Heatmap → Show values). One number per row at
      // the live-edge end of the flow ribbon — never hist columns (those
      // reposted every candle into a smear) and never recentered every frame
      // along a growing bar (that left trails when anything blitted).
      const wantValues = !!showValuesRef?.current
      const valueByRow = wantValues ? new Map() : null  // r -> {x,y,rw,rh,tot,edge}
      const pushHeatValue = (x, y, rw, rh, tot, liveEdgeX) => {
        if (!valueByRow || !(tot > 0) || rw < 28 || rh < 9) return
        const r = Math.round(y / Math.max(1e-6, rh))
        const edge = liveEdgeX
        const prev = valueByRow.get(r)
        // Prefer the segment whose live edge is closest to the seam (newest).
        if (prev && Math.abs(prev.edge - seamX) <= Math.abs(edge - seamX) && prev.rw >= rw) return
        valueByRow.set(r, { x, y, rw, rh, tot, edge })
      }
      const fmtHeatVal = (v) => (
        v >= 1e6 ? `${(v / 1e6).toFixed(1)}M`
        : v >= 1000 ? `${(v / 1000).toFixed(1)}k`
        : v >= 10 ? `${Math.round(v)}`
        : v.toFixed(1)
      )
      const paintHeatValuesOnto = (c) => {
        if (!valueByRow || valueByRow.size === 0 || !c) return
        c.save()
        c.textAlign = 'center'
        c.textBaseline = 'middle'
        for (const { x, y, rw, rh, tot, edge } of valueByRow.values()) {
          const fs = Math.max(8, Math.min(11, rh * 0.7))
          // (perf3) Per ROW, usually the same size — assign only on change.
          setCtxFont(c, `600 ${fs}px 'JetBrains Mono', monospace`)
          // Anchor near the live edge of the bar, inset so it stays inside.
          const inset = Math.min(rw * 0.5, Math.max(10, fs * 1.6))
          const cx = edge <= x + rw * 0.5 ? x + inset : x + rw - inset
          const cy = y + rh * 0.5
          const label = fmtHeatVal(tot)
          c.lineWidth = 2.5
          c.strokeStyle = 'rgba(0,0,0,0.65)'
          c.strokeText(label, cx, cy)
          c.fillStyle = 'rgba(255,255,255,0.92)'
          c.fillText(label, cx, cy)
        }
        c.restore()
      }
      // Shared-scene heat lives in the FBO — stamp labels there so they blit
      // with the bars. Own-GL / 2D: paint on this layer's 2D canvas (above fills).
      const flushHeatValues = () => {
        // Own-canvas only: labels on the cleared 2D surface.
        if (!wantValues || !valueByRow || valueByRow.size === 0) {
          if (labelsOutRef) labelsOutRef.current = []
          return
        }
        if (labelsOutRef) labelsOutRef.current = []
        paintHeatValuesOnto(ctx)
      }
      let heatLayerOpen = false
      if (useGL) {
        if (useScene) {
          // FPCC owns beginFrame / default-FB clear. Heat writes into heat FBO only.
          heatLayerOpen = !!gl.beginHeatLayer()
          if (!heatLayerOpen) {
            // FBO unavailable — fall back to 2D fills this frame (outlines canvas).
            useGL = false
          }
        } else {
          // Own-GL: resize() early-outs when device px unchanged. (chart-perf)
          // Skipped entirely when w/bh/dpr match the last call for this
          // controller — this ran on every heat paint (every frame).
          const _gs = glSizeMemo
          if (_gs.gl !== gl || _gs.w !== w || _gs.h !== bh || _gs.dpr !== dpr) {
            gl.resize(w, bh, dpr)
            gl.setCssSize(w, bh)
            _gs.gl = gl; _gs.w = w; _gs.h = bh; _gs.dpr = dpr
          }
          heatLayerOpen = true
        }
      }

      const RH = geom.rowHeight
      // ONE row function: the chart's own membership (proj.rowOfPrice, via
      // getGeom). Row i holds [key_i, key_i + step) — exactly the cells.
      // Fallback only: priceToY is row-EDGE (key at the row's bottom, y =
      // (row+1)*RH), so membership is ceil(y/RH - 1). Matches rowOfPrice.
      const rowOf = (typeof geom.rowOfPrice === 'function')
        ? geom.rowOfPrice
        : (p) => Math.ceil(s.priceToY(p) / RH - 1 - 1e-9)
      // REG through the SAME pipeline as the main canvas: a CSS transform
      // on the element, NOT an offset baked into draw coordinates. The
      // compositor shifts a finished bitmap; draw-time offsets antialias
      // at the fractional boundary — same number, two pipelines, up to a
      // pixel apart whenever REG is fractional. Visible exactly on the
      // brightest bands. Identical value + identical mechanism = pixel-
      // identical placement, by construction.
      // transform write happens below, once xRem for THIS frame is known
      // (translate(xRem, REG) — one property, write-on-change).
      // ── x: canvas-anchored, fixed one-column strip ──
      // Age 0 sits exactly at the canvas's own seam (both sides compute
      // that edge with the same formulas rather than two derivations).
      const seamX = (typeof s.getSeamX === 'function') ? s.getSeamX() : 0
      // ── ONE RULER (per Levi, the final spec) ──
      // Bands move at the TRAIL'S speed, linearly, everywhere — pxPerMs =
      // w / TRAIL_WINDOW_MS — so dots and bands from the same instant sit
      // at the same x for their whole lives, not just at the seam. The
      // "Extend fwd" knob sets the DROP-OFF in seconds, capped at the
      // trail's own window: knob at the cap = the flow spans exactly as
      // far as the trail does. Length is the USER'S, from the menu; the
      // code stopped having opinions about it. (The one-column and
      // ease-curve chapters solved specs of my own construction — each
      // patch had narrowed to the latest complaint instead of summing
      // them. Deleted whole.)
      const cw = geom.cw || 0
      const extN = extendRef ? (extendRef.current | 0) : 0
      const dir = s.flowDir === 'right' ? 1 : -1
      const pxPerMs = w / TRAIL_WINDOW_MS
      const T = Math.min(extN * 1000, TRAIL_WINDOW_MS)   // persistence, capped at the trail's span
      const dropPx = T * pxPerMs                          // knob-at-cap ⇒ dropPx = w = trail length
      // DEVICE-GRID QUANTIZED — the chart's own _dprQ doctrine (see the
      // pan-shimmer comment in FPCC): every x lands on a device-pixel
      // boundary, motion advances in 1/dpr steps finer than the eye, and
      // stripe joints CANNOT antialias because nothing is ever fractional.
      // Probe: useGL is final by here (the beginHeatLayer failure path above
      // is the last thing that can clear it). Field writes on a persistent
      // object — no allocation, no branching.
      HEAT_PROBE.enabled = isFootprintCellGLEnabled()
      HEAT_PROBE.mode = useScene ? 'scene' : (useGL ? 'own' : '2d')
      HEAT_PROBE.ok = !!(gl && gl.ok)
      HEAT_PROBE.lost = !!(gl && gl.lost)
      HEAT_PROBE.colors = 0
      HEAT_PROBE.rects = 0
      HEAT_PROBE.paints++
      HEAT_PROBE.ts = now

      // ── SUB-PIXEL UNDER GL, DEVICE-SNAPPED ON 2D ──────────────────────
      // The snap is the comb fix: two adjacent runs must round their shared
      // boundary to the SAME integer device column, or 2D fills each cover
      // that pixel partially, the two partial coverages do not sum back to
      // one, and the joint shows as a translucent hairline.
      //
      // It is also what quantised the motion. pxPerMs is w/30000, so the
      // ribbon wants 1.76 device px per frame at dpr 2 and the snap makes it
      // step 1 or 2. Bounded and drift-free since the blit came out, but
      // still a step.
      //
      // GL does not need the snap. With antialias:false the top-left fill
      // rule assigns every pixel on a shared edge to exactly ONE triangle —
      // no gap and no double-blend — so runs meeting at a fractional x
      // rasterise seamlessly and the ribbon can move by true sub-pixel
      // amounts. That is why the context requests antialias:false: under
      // MSAA both sides of a fractional edge get partial coverage and the
      // hairline comes straight back, which is the whole reason this was
      // snapped in the first place.
      //
      // Read at CALL time, not captured: useGL is still mutable when this is
      // defined (the beginHeatLayer failure path clears it above), and a
      // frame that falls back to 2D must snap.
      const xOfTs = (t) => {
        const x = seamX + dir * (now - t) * pxPerMs
        return useGL ? x : Math.round(x * dpr) / dpr
      }

      // ── Fill LUT (shared grammar with the main pass) ──
      const tLo = loRef.current
      const tHi = Math.max(tLo + 0.02, hiRef.current)
      const HN = normRef.current
      // Mutable: hist samples → folds/snaps → refreshes before paint so
      // scroll into a denser book does not flash every-row-max on a stale span.
      let nLo = HN.lo, nSpan = HN.span
      const ramp = rampRef.current
      const gam = Math.max(0.2, Math.min(4, (gammaRef && gammaRef.current) || 1))
      // Norm terms deliberately NOT in the key: the LUT maps u -> color and
      // never depended on the normalizer; keying on it forced pointless
      // rebuilds every time calibration breathed.
      const lutKey = `${tLo.toFixed(3)}|${tHi.toFixed(3)}|${ramp && ramp.join(',')}|g${gam.toFixed(2)}`
      let LUT = lutRef.current.lut
      if (lutRef.current.key !== lutKey) {
        const stops = RAMP_POS.map((p, gi) => {
          const [rr, gg, bb] = hexRgb(ramp?.[gi])
          return [p, rr, gg, bb]
        })
        LUT = new Array(33)
        for (let q = 0; q <= 32; q++) {
          const u = Math.pow(q / 32, gam)
          let j = 1
          while (j < stops.length - 1 && stops[j][0] < u) j++
          const A = stops[j - 1], B = stops[j]
          const f = (u - A[0]) / (B[0] - A[0] || 1)
          const rC = Math.round(A[1] + (B[1] - A[1]) * f)
          const gC = Math.round(A[2] + (B[2] - A[2]) * f)
          const bC = Math.round(A[3] + (B[3] - A[3]) * f)
          const a = 0.22 + 0.78 * Math.pow(u, 1.15)
          LUT[q] = `rgba(${rC},${gC},${bC},${a.toFixed(3)})`
        }
        lutRef.current = { key: lutKey, lut: LUT }
      }
      // t (log-normalized size) -> LUT bucket; -1 = below floor, not drawn.
      // Takes log1p(size) — entries carry it precomputed (st.rl), so the
      // walks do no transcendental per row per frame.
      const bucketOfL = (lg) => {
        const span = (nSpan > 0 && Number.isFinite(nSpan)) ? nSpan : Math.LN2
        const t = (lg - nLo) / span
        if (t < tLo) return -1
        const u = Math.max(0, Math.min(1, (t - tLo) / (tHi - tLo)))
        return Math.round(u * 32)
      }

      const LUTREF = lutRef.current.lut
      const F = bk.slot          // tf-keyed slot fold, advanced by FPCC's heat pass
      // ── HISTORY: the per-entry row scratch that used to live here ──────
      // Two structures have now been deleted from this spot, and the reason
      // matters because both look like obvious things to add back.
      //
      // First was `const rowTot = new Map()`, cleared and refilled inside
      // rowsOf on EVERY call, cache hit included — profiled at ~2.0s of self
      // time, 22% of a recording, against ~126ms for every canvas op
      // combined. clear()+N×set() per entry per frame is close to the worst
      // use of a Map: the backing store churns on every cycle and every
      // insert hashes.
      //
      // It was replaced by a stamped dense array (_rsLoad/_rsGet), which was
      // faster but still wrote one slot per row per entry per frame. Both
      // existed for ONE consumer: the O(1) get() in the walk's open-run
      // scan. That scan is gone — the walk now iterates st.rr/st.rt directly
      // and finds vanished rows by generation stamp — so the scratch has no
      // consumer left.
      //
      // st.rr/st.rt are already parallel typed arrays holding exactly these
      // pairs. Anything that wants row totals for an entry should read them
      // rather than build a third copy.
      // Price→row projection, CACHED PER ENTRY and invalidated by the
      // lattice key. Profiling put paintNow at 51% of frame time with
      // almost none of it in canvas ops: the cost was calling priceToY
      // for every level of every flow entry on every paint (~60 × ~300 ×
      // 30fps). A flow entry's price levels never change once folded, so
      // its row projection is derivable ONCE and reusable until the
      // lattice itself moves (rebucket — rare). The blit fixed
      // rasterization and left this derive running at full rate; that was
      // the miss, and it is the same "cache the derive, invalidate on the
      // thing it actually depends on" shape as the main fold.
      // Owner id from getGeom (geom.latticeId); latticeKey kept as fallback
      // for older publishers. Same cached lattice array → same id → st.rk
      // stable across idle slides; structural rebuild mints a new id.
      const latticeKey = geom.latticeId != null ? geom.latticeId : (geom.latticeKey || '')
      // ── ROW STEP: what makes a cached projection adoptable by a shift ──
      // rowOfPrice = ceil((top − W(p))/step − ε) depends on top and step
      // only, so on the SAME step any lattice change (row roll, new top,
      // new id, row-height zoom) moves every cached row by one integer k.
      // A step change (rebucket / refine) is a rescale and must re-project.
      // This used to be keyed on RH: an RH change with the same step was
      // needlessly re-projected, and a step change at an unchanged RH could
      // be adopted as a shift whenever an entry's two probes happened to
      // agree (always, for a one-row entry). The fallback rowOf divides by
      // RH, so it keys on RH (negated so the two can never compare equal).
      const stepKey = (typeof geom.rowOfPrice === 'function' && geom.rowStep > 0) ? geom.rowStep : -RH
      const sameStep = (a) => a === stepKey
        || (a > 0 && stepKey > 0 && Math.abs(a - stepKey) <= 1e-9 * stepKey)
      // ── PROJECTION: unbudgeted, in the frame it is needed ─────────────
      // This was budgeted (8 entries/paint) and an unprojected entry simply
      // did not paint. Every rebucket / bucket-step change / Y-refine
      // re-keys the step, so the shift path below cannot adopt and the whole
      // deque went cold at once: the ribbon painted 8 entries a frame,
      // newest-first, and the rest of it blinked out for N/8 frames — the
      // flash on rebucket. The budget existed because a projection cost a
      // Map, two typed-array allocations and TWO rowOf passes per price;
      // this costs one rowOf per price into reused scratch plus one dense
      // accumulate, cheap enough to do the whole deque in one frame.
      const projectEntry = (st) => {
        const px = st.px, tv = st.tot, n = px.length
        if (_rowScr.length < n) _rowScr = new Float64Array(Math.max(n, _rowScr.length * 2))
        let lo = Infinity, hi = -Infinity, pLo = null, pHi = null
        for (let j = 0; j < n; j++) {
          const p = px[j]
          let r = NaN
          if (p > 0) {
            r = rowOf(p)   // row CONTAINING the price — the cells' membership
            if (r < lo) { lo = r; pLo = p }
            if (r > hi) { hi = r; pHi = p }
          }
          _rowScr[j] = r
        }
        let rr, rt
        const span = hi - lo + 1
        if (!(span >= 1)) {                       // nothing projected (empty / all NaN)
          rr = new Int32Array(0); rt = new Float64Array(0); lo = 0; hi = 0
        } else if (span <= 65536) {
          if (_accScr.length < span) {
            const L = Math.max(span, _accScr.length * 2)
            _accScr = new Float64Array(L); _hitScr = new Uint8Array(L)
          } else {
            _accScr.fill(0, 0, span); _hitScr.fill(0, 0, span)
          }
          let m = 0
          for (let j = 0; j < n; j++) {
            const r = _rowScr[j]
            if (r !== r) continue                 // NaN = unprojected price
            const o = r - lo
            if (!_hitScr[o]) { _hitScr[o] = 1; m++ }
            _accScr[o] += tv[j]                   // same summation order as the Map
          }
          rr = new Int32Array(m); rt = new Float64Array(m)
          for (let o = 0, k = 0; o < span; o++) {
            if (_hitScr[o]) { rr[k] = o + lo; rt[k] = _accScr[o]; k++ }
          }
        } else {
          // Pathological spread (stale far-off levels on a fine step): a
          // dense span would be huge, so fall back to the sparse fold.
          const mp = new Map()
          for (let j = 0; j < n; j++) {
            const r = _rowScr[j]
            if (r !== r) continue
            mp.set(r, (mp.get(r) || 0) + tv[j])
          }
          rr = new Int32Array(mp.size); rt = new Float64Array(mp.size)
          let k = 0
          for (const [r, t] of mp) { rr[k] = r; rt[k] = t; k++ }
        }
        const rl = new Float64Array(rr.length)
        for (let k = 0; k < rr.length; k++) rl[k] = Math.log1p(rt[k])
        st.rr = rr; st.rt = rt; st.rl = rl
        st.rlo = lo; st.rhi = hi
        // Shift-probe anchors: the prices on the entry's extreme rows, the
        // widest baseline its own data offers for telling a shift from a
        // rescale (backup to the step key, which is the primary guard).
        st.rp = pLo; st.rpr = lo
        if (pHi != null && pHi !== pLo) { st.rp2 = pHi; st.rpr2 = hi } else { st.rp2 = null; st.rpr2 = 0 }
        st.rStep = stepKey
        st.rk = latticeKey
      }
      const rowsOf = (st) => {
        if (st.rk === latticeKey) return true
        // ── ADOPT A SHIFT, DO NOT RE-DERIVE IT ────────────────────────
        // Same step ⇒ every cached row r becomes r + k for one k (see the
        // step note above). Probe the entry's own extreme prices to recover
        // k — an O(rows) integer add, so every entry adopts on the SAME
        // frame together with the candles. Two probes must agree.
        if (st.rr && st.rr.length && st.rp != null && sameStep(st.rStep)) {
          let k = rowOf(st.rp) - st.rpr
          if (st.rp2 != null && rowOf(st.rp2) - st.rpr2 !== k) k = null
          if (k !== null && Number.isFinite(k)) {
            if (k !== 0) {
              for (let j = 0; j < st.rr.length; j++) st.rr[j] += k
              st.rlo += k
              st.rhi += k
              st.rpr += k
              if (st.rp2 != null) st.rpr2 += k
            }
            st.rStep = stepKey
            st.rk = latticeKey
            return true
          }
        }
        projectEntry(st)
        return true
      }
      // ── PAINT CAMERA APPLIED AT ROW→PIXEL ──────────────────────────
      // Same transform the canvas applies in gctx.transform, at the same
      // stage: after the row grid, not folded into priceToY. It cannot go
      // into priceToY because the projection round-trips —
      // floor(priceToY(px)/RH) gives an integer row and yOfRow multiplies it
      // back up — so a cameraed priceToY would land on the wrong ROW, not
      // just the wrong pixel.
      //
      // Height scales with it: a row is RH tall in paint space and RH*scale
      // on screen, exactly as the canvas draws its cells. Identity while
      // idle (scale 1, ty 0), so no branch.
      //
      // This is what stops the double jump. The lattice ref moves FROM →
      // DEST → land underneath, and the canvas glides through it on the
      // camera; the layer now rides the same curve instead of snapping at
      // each phase boundary.
      const camS = Number.isFinite(geom.camScale) ? geom.camScale : 1
      const camT = Number.isFinite(geom.camTy) ? geom.camTy : 0
      const RHc = RH * camS
      const yOfRow = (r) => r * RH * camS + camT   // row TOP, camera applied. REG rides the element transform above

      // ── The flow: per-row RLE across time bands ──
      // Walk the deque oldest→newest; per row keep an open run {x0, q};
      // when a row's bucket changes (or the row empties) the run flushes as
      // ONE rect. Book levels persist, so runs collapse most bands — fills
      // scale with CHANGES, the same economy the slot pass gets from its
      // run merge. After the last band, every open run extends to x(now):
      // the live growth that makes the layer visibly flow.
      open.clear()   // effect-scope Map (see scratch block)
      // Run touch-stamp, bumped PER ENTRY. Lets the walk below detect
      // vanished rows by count instead of scanning open every entry.
      let _runGen = 0
      const flushRun = (r, run, x1) => {
        if (run.q < 0) return
        let a = Math.min(run.x0, x1), b = Math.max(run.x0, x1)
        if (b <= a) return
        // CONTINUOUS RIBBONS (per Levi, stated plainly and final): "the
        // bars stream off whatever is in the live column, continuously,
        // without gaps, until the data changes — then they change.
        // Nothing else." Segments run their TRUE length: a row's band is
        // attached at the seam while the level lives, extends as time
        // passes, and its color changes exactly where the data changed —
        // the new run opens at the same x the old one closes, so change-
        // points abut with zero gap. A gap appears only where the data
        // itself went away (bucket below the floor). The chip/one-column
        // chapters are deleted; they were my constructions, not the spec.
        pushHeatFill(LUT[run.q], a, yOfRow(r), b - a, RHc)
        // Live edge = end toward seam (xNow side). dir<0 → seam on the right (b).
        if (run.tot > 0) pushHeatValue(a, yOfRow(r), b - a, RHc, run.tot, dir < 0 ? b : a)
      }
      // Walk OLDEST -> NEWEST so runs open at the far (aged) end and close
      // toward the origin; per-direction clip keeps the layer strictly on
      // its own side of the live edge (the main canvas owns the other).
      // The canvas slot heat owns everything up to the seam; this field
      // owns from the seam to the knob's drop-off. Bands past it clamp to
      // the clip and flush zero-width: the cull is free. extN = 0 → off.
      if (extN <= 0) return
      let clipLo = dir > 0 ? Math.max(0, seamX) : Math.max(0, seamX - dropPx)
      let clipHi = dir > 0 ? Math.min(w, seamX + dropPx) : Math.min(w, seamX)
      // ═══ REGION SPLIT (merge step 3) ═══
      // The two halves of this canvas cache differently and must not
      // overlap: the flow band is pure ribbon and rides the pxPerMs blit;
      // history is anchored to candles and holds still until its own inputs
      // change. Overlapping them is what made the blit impossible on one
      // surface — and is also what produced the shade step, since an
      // aggregate column and a time-resolved ribbon disagree wherever they
      // cover the same pixels.
      // histEdge SNAPS TO A COLUMN BOUNDARY so the handoff lands on a candle
      // edge, where a closed column genuinely IS the slot's book — an honest
      // seam instead of two renderers arguing mid-column.
      const histWant = dir < 0 ? clipLo : Math.min(w, seamX)
      let histEdge = histWant
      if (geom.cols && geom.cols.cw > 0) {
        const C = geom.cols
        const k = Math.round((histWant - C.xOfCol(C.startIdx)) / C.cw) + C.startIdx
        const snapped = Math.round(C.xOfCol(k) * dpr) / dpr
        // The snap may only NUDGE the edge onto a candle boundary — never
        // relocate it. Unclamped it moved by up to cw/2, which at wide
        // columns (or a wide flow band, where histWant is already near 0)
        // pushed the edge to/past zero: history's region collapsed to zero
        // width and the whole slot map vanished while the flow kept
        // painting. Scale-dependent because cw sets the snap distance.
        if (Math.abs(snapped - histWant) <= C.cw) histEdge = snapped
      }
      // Hard bounds: history can never be given a negative region, and can
      // never be handed pixels the flow owns.
      histEdge = Math.max(0, Math.min(histEdge, Math.min(w, seamX)))
      // No flow at all (HL heat off / not yet streaming): history owns the
      // entire surface, including the projection region. The band collapses
      // to zero width, so the blit and its clears become no-ops instead of
      // scrubbing a region nothing repaints.
      if (!bk.flow.length) histEdge = w
      const histLo = -1, histHi = histEdge
      // Close the partition: the flow clips exactly where history stops.
      // histEdge snapped to a column boundary, so without this the two
      // regions leave a transparent sliver up to half a column wide — the
      // blit's far-edge scrub would keep re-clearing it every frame.
      if (geom.cols) {
        if (dir < 0) clipLo = Math.max(0, Math.min(histEdge, seamX))
        else clipHi = Math.min(w, Math.max(clipHi, histEdge))
      }
      // ── Blit decision ──
      const B = blitRef.current
      const fullKey = `${bk.gen}|${RH}|${cw.toFixed(4)}|${dropPx.toFixed(2)}|${dir}|${w}|${bh}|${lutKey}|${(geom.cols && Number.isFinite(geom.cols.panOffsetPx)) ? geom.cols.panOffsetPx.toFixed(3) : '0'}`
      const dt = B.lastNow ? now - B.lastNow : 0
      // time drift and pan are BOTH pure translations of painted content:
      // x = seamX + dir·age·px, so dt moves everything by dir·px·dt and a
      // seam move by Δseam moves everything by Δseam.
      const shiftWant = (Number.isFinite(B.lastSeam) ? seamX - B.lastSeam : 0) + dir * dt * pxPerMs + B.xRem
      // COLD = some entry not yet projected onto this lattice at frame
      // start (a lattice change, or the 2Hz append). Projection itself is
      // now synchronous (see projectEntry), so this only gates the blit:
      // rows that moved this frame invalidate the shifted pixels.
      //
      // TWO READS, NOT A SCAN. The cold set is always [cold prefix][warm
      // middle][cold suffix]: a latticeKey change makes EVERY entry cold at
      // once, appends land cold at the newest end, and every paint projects
      // whatever it walks. Testing both ends answers "is anything cold".
      const _fl = bk.flow.length
      const _cold = _fl > 0
        && (bk.flow[0].rk !== latticeKey || bk.flow[_fl - 1].rk !== latticeKey)
      // Own-GL draws the DEFAULT framebuffer with preserveDrawingBuffer:false
      // and is gl.clear()ed every paint, so there
      // is never a previous bitmap to shift. Blitting there was not a saving
      // but a bug: shiftCssRect moved cleared pixels, then the walk drew only
      // the last ~1–2 entries (walkFrom), so on blit frames the GL ribbon
      // collapsed to a sliver at the seam and came back whole on the next
      // cold (append) frame — a 2Hz flicker, worst while warming.
      const glOwnEphemeral = useGL && !useScene
      let canBlit = !_resized && !_cold && !glOwnEphemeral && B.key === fullKey
        && Number.isFinite(B.lastSeam) && Math.abs(shiftWant) < w / 2
      // Value labels smear under blit; GL can shiftCssRect the flow band.
      if (wantValues) canBlit = false
      B.key = fullKey; B.lastNow = now; B.lastSeam = seamX
      let stripA = clipLo, stripB = clipHi          // full path repaints everything
      if (canBlit) {
        // integer DEVICE pixels only — fractional drawImage resamples and
        // the blur compounds every frame. The remainder is an ACCUMULATOR
        // and nothing more: it is never rendered anywhere (the earlier
        // element-transform carry re-fractionalized every joint — the
        // worsened comb). It simply rolls into the next frame's shift.
        const shiftDev = Math.round(shiftWant * dpr)
        const shiftPx = shiftDev / dpr
        B.xRem = shiftWant - shiftPx
        if (shiftDev !== 0) {
          if (useGL && gl && typeof gl.shiftCssRect === 'function') {
            // GL band shift — same CSS region as the 2D blit source.
            const bx = Math.min(histEdge, clipLo)
            const bw = clipHi - bx
            gl.shiftCssRect(bx, -HEAT_OVERDRAW, Math.max(0, bw), bh, shiftPx)
          } else {
            ctx.save()
            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.globalCompositeOperation = 'copy'   // vacated region becomes transparent in one op
            // REGION blit: source rect = the flow band only, so history
            // pixels outside it are never touched by the flow's shift.
            const bLo = Math.max(0, Math.floor(Math.min(histEdge, clipLo) * dpr))
            const bHi = Math.min(canvas.width, Math.ceil(clipHi * dpr))
            const bW = Math.max(0, bHi - bLo)
            if (bW > 0) {
              // CLIP THE DESTINATION TO THE BAND. The shift is negative for
              // leftward flow, so dest = bLo + shiftDev lands LEFT of the
              // boundary: without this the band bleeds a few pixels of ribbon
              // into history every blit frame. History only repaints when its
              // own key changes, so on a still chart the erosion accumulated
              // frame after frame and ate the slot map from the boundary
              // leftward — visible "at a certain point", restored by any pan
              // or zoom. Clipping bounds the copy to the region that owns it.
              ctx.beginPath()
              ctx.rect(bLo, 0, bW, canvas.height)
              ctx.clip()
              ctx.drawImage(canvas, bLo, 0, bW, canvas.height,
                                    bLo + shiftDev, 0, bW, canvas.height)
            }
            ctx.restore()
            ctx.setTransform(dpr, 0, 0, dpr, 0, dpr * HEAT_OVERDRAW)
          }
        }
        // repaint only the seam sliver (young side) + scrub the far edge
        const pad = 2
        if (dir > 0) { stripA = clipLo; stripB = Math.min(clipHi, clipLo + Math.abs(shiftPx) + pad) }
        else { stripB = clipHi; stripA = Math.max(clipLo, clipHi - Math.abs(shiftPx) - pad) }
      } else {
        B.xRem = 0
      }
      // ── DEVICE-GRID DISCIPLINE (the comb fix) ──
      // All x is quantised to the device grid in xOfTs, so adjacent runs round
      // the SAME boundary to the same integer device column and no joint can
      // antialias into a translucent hairline. Blit path: round the shift,
      // accumulate the remainder invisibly. Everything — old content, new
      // slivers, clip joints — lives on integer device columns.
      ctx.setTransform(dpr, 0, 0, dpr, 0, dpr * HEAT_OVERDRAW)
      const _snapLo = Math.floor(stripA * dpr) / dpr
      const _snapHi = Math.ceil(stripB * dpr) / dpr
      if (canBlit) {
        ctx.clearRect(_snapLo, -HEAT_OVERDRAW, _snapHi - _snapLo, bh)
        // stale content shifted past the drop-off (1px pad covers the frame offset):
        // Scrub only INSIDE the band — the far-edge clear used to run to
        // x=-1 and would now erase history.
        if (dir > 0) ctx.clearRect(clipHi, -HEAT_OVERDRAW, w - clipHi + 1, bh)
        else ctx.clearRect(histEdge, -HEAT_OVERDRAW, Math.max(0, clipLo - histEdge) + 1, bh)
      } else {
        // Full flow repaint: clear the BAND only. History is cleared by its
        // own dirty path below, never by the flow's.
        ctx.clearRect(histEdge, -HEAT_OVERDRAW, w - histEdge + 2, bh)
      }
      // element transform: REG only — the x remainder is never rendered
      const _tf = `translateY(${REG}px)`
      if (_tf !== lastRegRef.current) {
        canvas.style.transform = _tf
        lastRegRef.current = _tf
      }
      if (glCanvas) glCanvas.style.transform = lastRegRef.current || _tf
      // ═══ HISTORY PASS (merge step 2) ═══
      // The slot-column heat, ported from FPCC's drawCanvas so ONE canvas
      // carries the whole liquidity picture. Faithful port first: it paints
      // exactly the columns the canvas painted, so this step is judged on
      // visual parity alone — the handoff refinement (who owns the forming
      // column) is a separate verified change once both live here.
      //
      // Geometry comes from geom.cols, published by FPCC — the same
      // one-mapping-one-owner rule as priceToY and getSeamX. xOfCol there is
      // character-identical to the canvas formula (verified at the publish
      // site); device-grid snapping stays here at the FILL, where the
      // translucent-hairline fix belongs.
      //
      // Drawn BEFORE the flow and outside the blit strip clip: history is
      // anchored to candles, not to the flow's pxPerMs ruler, so it must not
      // ride the flow's shift. Step 3 gives it its own dirty key + region
      // blit; for now it repaints each frame, which is what the canvas pass
      // already did per full draw.
      const cols = geom.cols
      // The forming CB column folds snaps with ts >= now − CB_LIVE_MS, which
      // moves with the clock, not with any key input — index of the first
      // snap inside that window makes the key exact for it.
      const _cbR = cbSnapsRef?.current || []
      let _cbLive = 0
      if (_cbR.length) {
        let a = 0, b = _cbR.length
        const t0 = now - CB_LIVE_MS
        while (a < b) { const m = (a + b) >> 1; if (_cbR[m].ts < t0) a = m + 1; else b = m }
        _cbLive = a
      }
      // DIRTY KEY: every input the history render reads. Unchanged ⇒ its
      // pixels are still correct and are left completely alone, which is
      // what makes one canvas as cheap as two.
      const histKey = cols
        ? `${bk.gen}|${RH}|${cols.cw.toFixed(4)}|${cols.startIdx}|${cols.endIdx}|${cols.panOffsetPx.toFixed(3)}|${latticeKey}|${F.cursor}|${F.slotArr.length}|${lutKey}|${histEdge.toFixed(2)}|${bh}|cb${_cbR.length}.${_cbLive}.${(cbMaxSzRef?.current) || 1}`
        : ''
      // ── NORM IS NOT IN THE KEY (and cannot be) ──
      // This pass WRITES normRef at the percentile fold below, so nLo/nSpan
      // are outputs of the very render they were keying. An EMA (α .25)
      // chasing a target that every 2Hz book capture moves again never
      // settles to four decimals on live tape, so `histKey !== B.histKey`
      // was true essentially every frame: the region repainted forever and
      // the key's promise — unchanged ⇒ pixels still correct — could not be
      // true. lutKey learned this exact lesson already (see its note).
      //
      // The replacement asks the only question that matters: could the norm
      // have moved far enough to CHANGE A PIXEL? Colour comes from
      // q = round(u·32), u = (t − tLo)/(tHi − tLo), t = (v − nLo)/nSpan, so
      // one bucket step is 1/32 of u, and
      //     Δt ≈ −Δlo/nSpan − t·Δspan/nSpan,   |t| ≤ tHi for any row that
      // isn't already clamped at the top of the ramp. Half a bucket of u is
      // therefore worth (|Δlo| + tHi·|Δspan|) = nSpan·(tHi − tLo)/64 — and
      // the tolerance tightens by itself when the sensitivity window is
      // narrow, because that is exactly when small norm moves do shift
      // buckets. The guarantee is "no row's colour is more than one bucket
      // step stale", not "no pixel differs": a row sitting on a boundary can
      // flip one step late, which on a 33-stop ramp fed by a smoothed
      // calibration is not visible. Repainting every frame to chase moves
      // ~400× finer than the ramp can render was.
      // Regime-scale only. Soft EMA after paint used to trip this every
      // book tick → histDirty forever → with sample+paint, 2× priceToY and
      // a pinned main thread. Live flow still reads breathing HN each frame;
      // hist stays put until the book regime actually jumps.
      const _normMoved = !Number.isFinite(B.hnLo) || !Number.isFinite(B.hnSpan)
        || !B.hnSpan || !nSpan
        || nSpan > B.hnSpan * 1.6 || B.hnSpan > nSpan * 1.6
        || Math.abs(nLo - B.hnLo) > nSpan * 0.5
      // Dirty when the history fingerprint / normalizer / canvas size moved,
      // OR (2D only) when the flow did not blit — a non-blit 2D frame clears
      // the band and must not leave history assuming persistence across a
      // full-band wipe that never happened to history. Under scene-GL (heat
      // FBO) history skips when histKey/norm are stable.
      //
      // OWN-GL (the live mount): the default framebuffer is discarded after
      // every present, so history must be RESUBMITTED every frame — but not
      // RE-WALKED. The walk (every visible column × every row, plus the CB
      // fold and a 4096-sample sort) used to run 30×/s for identical output.
      // Its fill batches are kept on B.hb/B.hb055 and replayed while the key
      // holds. Own-GL's history has always breathed with the per-frame norm,
      // so its tolerance is the half-bucket rule from the note above rather
      // than the regime test: replayed pixels are never more than half a
      // ramp step from what a re-walk would draw.
      // ── HEAT-FLASH FIX (2026-10-18): EASE THE NORMALIZER, DON'T SNAP IT ──
      // The window used to SNAP to the new p25/p97 whenever they moved past
      // 1.6x span / half a span ("regime"). Panning or scrolling brings new
      // columns/levels into the sample, which crossed that line routinely,
      // so every row's colour jumped at once — often twice (one stale frame
      // painted on the pre-snap, then the post-walk snap): the flash.
      // Now: a hard snap only on a true reset (HN zeroed on coin switch /
      // heat toggle) or an extreme jump (4x span / 2 spans); everything else
      // eases on wall time (tau 140ms, ~90% in ~320ms) so new levels fade
      // the scale over a few frames instead of popping it. dt is capped at
      // 50ms so the first fold after an interaction freeze can't take one
      // huge step. Same fold count per paint as before -> no extra walks.
      const HN_TAU_MS = 140
      const _hnHuge = (p25, spanRaw) =>
        !HN.span ||
        !Number.isFinite(HN.lo) ||
        spanRaw > HN.span * 4 ||
        HN.span > spanRaw * 4 ||
        Math.abs(p25 - HN.lo) > spanRaw * 2
      const _foldHN = (p25, p97) => {
        const spanRaw = Math.max(Math.LN2, p97 - p25)
        const tNow = performance.now()
        const dtF = Number.isFinite(B.hnTs) ? Math.min(50, Math.max(0, tNow - B.hnTs)) : 0
        B.hnTs = tNow
        if (_hnHuge(p25, spanRaw)) { HN.lo = p25; HN.span = spanRaw; return }
        const a = 1 - Math.exp(-dtF / HN_TAU_MS)
        HN.lo = HN.lo + a * (p25 - HN.lo)
        HN.span = HN.span + a * (spanRaw - HN.span)
      }
      const _preSnapHN = () => {
        if (!(Number.isFinite(B.p25) && Number.isFinite(B.p97) && normRef)) return
        const spanRaw = Math.max(Math.LN2, B.p97 - B.p25)
        // Only a true reset / extreme jump pre-snaps (see the note above).
        const regime = _hnHuge(B.p25, spanRaw)
        if (regime) {
          HN.lo = B.p25
          HN.span = spanRaw
          nLo = HN.lo
          nSpan = HN.span
        }
      }
      let histDirty
      let histReplay = false
      if (glOwnEphemeral) {
        _preSnapHN()   // what the walk would do first; idempotent
        const _tol = nSpan * (tHi - tLo) / 64
        const _normDrift = !Number.isFinite(B.hnLo) || !Number.isFinite(B.hnSpan) || !(nSpan > 0)
          || (Math.abs(nLo - B.hnLo) + tHi * Math.abs(nSpan - B.hnSpan)) > _tol
        histDirty = histKey !== '' && (histKey !== B.histKey || _resized || _normDrift || !B.hb)
        histReplay = histKey !== '' && !histDirty
      } else {
        histDirty = histKey !== '' && (
          histKey !== B.histKey || _normMoved || _resized
          || (!useGL && !canBlit)
        )
      }
      if (!B.hb) { B.hb = new Map(); B.hb055 = new Map() }
      if (histDirty) { B.hb.clear(); B.hb055.clear() }
      const histBatch = B.hb, histBatch055 = B.hb055
      HEAT_PROBE.hist = histDirty
      // Under useGL: histDirty → full clear; canBlit → seam strip + far-edge
      // scrub (mirror 2D); else → full flow band. History survives when stable.
      if (useGL && gl) {
        if (histDirty || glOwnEphemeral) {
          gl.clear()
        } else if (canBlit && typeof gl.clearCssRect === 'function') {
          gl.clearCssRect(_snapLo, -HEAT_OVERDRAW, _snapHi - _snapLo, bh)
          if (dir > 0) gl.clearCssRect(clipHi, -HEAT_OVERDRAW, w - clipHi + 1, bh)
          else gl.clearCssRect(histEdge, -HEAT_OVERDRAW, Math.max(0, clipLo - histEdge) + 1, bh)
        } else if (typeof gl.clearCssRect === 'function') {
          gl.clearCssRect(histEdge, -HEAT_OVERDRAW, Math.max(0, w - histEdge) + 2, bh)
        }
      }
      // Key is committed only once the pass has finished (below): a walk
      // that throws must not leave a half-filled batch looking clean.
      if (histDirty) B.histKey = ''
      if (histDirty && cols && F && (F.slotArr.length || F.lastSlot >= 0 || (cbSnapsRef?.current || []).length)) {
        ctx.clearRect(histLo, -HEAT_OVERDRAW, Math.max(0, histHi - histLo), bh)
        // try/finally AROUND EVERYTHING INSIDE THE CLIP.
        // The region is cleared on the line above, so a throw anywhere in the
        // pass below used to mean: cleared, nothing painted back, restore()
        // never reached. The clip then leaked into every subsequent frame on
        // this context — one bad column permanently corrupting the layer.
        // That is exactly what colTime's undefined element did, ~24x/second.
        //
        // colTime is total now, but the shape of the failure is the problem:
        // a single bad value should cost a column, not the canvas. finally
        // guarantees the context stack unwinds whatever the pass does.
        // Single hist walk. Always-on sample→paint doubled priceToY/mcapToY
        // and, with soft-EMA histDirty, pinned the main thread (~40% mcapToY).
        // Flash fix without the 2× cost: pre-snap from the PREVIOUS pass's
        // p25/p97 when the book regime jumped, paint once (sampling as we
        // go), fold after. At most one stale frame into a denser book.
        const _hnBuf = hnScratchRef.current
        let _hnN = 0
        // Uniform sample of the WHOLE visible set. This used to keep only
        // the first 4096 samples (= the leftmost columns when zoomed out),
        // so panning swapped the sample wholesale and the scale lurched.
        // Reservoir with a fixed-seed LCG: deterministic for identical
        // input (no jitter on a still chart), O(1) per row, no allocation.
        const _hnCap = _hnBuf.length
        let _hnSeen = 0, _hnSeed = 0x9e3779b9
        const _hnPush = (lg) => {
          if (_hnN < _hnCap) _hnBuf[_hnN++] = lg
          else {
            _hnSeed = (Math.imul(_hnSeed, 1664525) + 1013904223) >>> 0
            const j = Math.floor((_hnSeed / 4294967296) * (_hnSeen + 1))
            if (j < _hnCap) _hnBuf[j] = lg
          }
          _hnSeen++
        }
        _preSnapHN()
        // Live column only: F.px mutates, so it is folded per pass. Closed
        // states are immutable and read their cached st.rr/st.rl directly
        // (same lattice cache as the flow) — no per-run Map copy.
        const _accTot = new Map()
        // ── SLIDE-OFF FIX (2026-10-18): ±1 COLUMN OVERDRAW, like candles ──
        // startIdx..endIdx are the N columns the chart counts as on-screen,
        // but panOffsetPx ∈ [0, cw) slides column startIdx-1 partly into
        // view at the left edge (and endIdx+1 at the right under the X
        // camera). FPCC draws candles over startIdx-1..endIdx+1 for exactly
        // this; heat walked only startIdx..endIdx, so the edge column was
        // never drawn while partly visible and popped in/out a whole column
        // at a time on pan. Same overdraw here; x still comes from xOfCol
        // (fractional pan, device-snapped at the fill) and the canvas /
        // history clip trims the partial column, so it slides off.
        const loI = Math.max(0, cols.startIdx - 1)
        const lastI = Math.min(cols.endIdx + 1, cols.total - 1)
        const firstSlotT = cols.colTime(loI) ?? 0
        const LIVE_STATE = { live: true }   // sentinel reference for run identity
        const walkHistRuns = (onRun) => {
          let sLo = 0, sHi = F.slotArr.length - 1, sIdx = -1
          while (sLo <= sHi) { const m = (sLo + sHi) >> 1; if (F.slotArr[m] <= firstSlotT) { sIdx = m; sLo = m + 1 } else sHi = m - 1 }
          let runStartI = -1, runState = null
          const _flush = (endI) => {
            if (runStartI < 0 || !runState) return
            // Device-grid snap: adjacent runs round the same boundary
            // identically, so no pixel is partially covered twice (the
            // translucent-hairline rule).
            const x0 = Math.round(cols.xOfCol(runStartI) * dpr) / dpr
            const wRun = Math.round((cols.xOfCol(endI) + cols.cw) * dpr) / dpr - x0
            if (wRun <= 0) { runStartI = -1; return }
            if (runState === LIVE_STATE) {
              _accTot.clear()
              for (const [px, tot] of F.px) {
                const r = rowOf(px)
                _accTot.set(r, (_accTot.get(r) || 0) + tot)
              }
              onRun(x0, wRun, null)
            } else {
              rowsOf(runState)
              onRun(x0, wRun, runState)
            }
            runStartI = -1
          }
          for (let i = loI; i <= lastI; i++) {
            const slotT = cols.colTime(i)
            if (slotT == null) continue
            while (sIdx + 1 < F.slotArr.length && F.slotArr[sIdx + 1] <= slotT) sIdx++
            const st = (F.lastSlot >= 0 && slotT >= F.lastSlot) ? LIVE_STATE
                     : (sIdx >= 0 ? F.stateArr[sIdx] : null)
            if (st !== runState) {
              _flush(i - 1)
              runState = st
              if (st) runStartI = i
            }
          }
          _flush(lastI)
        }

        // try/finally AROUND EVERYTHING INSIDE THE CLIP.
        // The region is cleared above; a throw used to leak the clip.
        ctx.save()
        ctx.beginPath()
        ctx.rect(histLo, -HEAT_OVERDRAW, Math.max(0, histHi - histLo), bh)
        ctx.clip()
        try {
        walkHistRuns((x0, wRun, st) => {
          // no value labels on hist columns — they reposted every candle
          if (st) {
            const rr = st.rr, rl = st.rl
            for (let j = 0; j < rr.length; j++) {
              const lg = rl[j]
              _hnPush(lg)
              const q = bucketOfL(lg)
              if (q < 0) continue
              pushHeatFill(LUTREF[q], x0, rr[j] * RH, wRun, RH, histBatch)
            }
          } else {
            for (const [r, tot] of _accTot) {
              const lg = Math.log1p(tot)
              _hnPush(lg)
              const q = bucketOfL(lg)
              if (q < 0) continue
              pushHeatFill(LUTREF[q], x0, r * RH, wRun, RH, histBatch)
            }
          }
        })
        } finally {
          ctx.restore()
        }
        // ── CB PASS (ported; the CB rewrite the AUTOF comment promised) ──
        // Same column walk as FPCC's, minus the AUTOF machinery — AUTOF was
        // pinned false, so colSig/contactPass/shelves never ran in
        // production and are NOT ported (dead paths deleted, not moved).
        // MAX-normalized against cbMaxSzRef (see the prop note). Slot
        // boundaries from cols.colTime — the same one-owner mapping.
        const cbRing = _cbR
        if (cbRing.length > 0) {
          const lgMaxCb = Math.log1p((cbMaxSzRef?.current) || 1)
          const cbQ = (sz) => {
            const t = Math.log1p(sz) / lgMaxCb
            if (t < tLo) return -1
            const u = Math.max(0, Math.min(1, (t - tLo) / (tHi - tLo)))
            return Math.round(u * 32)
          }
          const _cbRows = new Map(), _cbSide = new Map()
          // Snapshots never change once taken, so their rows are derived once
          // per lattice — flat [row, size, side] triples, bids then asks, the
          // same order the max-per-row fold below relies on for ties.
          const cbRowsOf = (sn) => {
            if (sn._rk === latticeKey && sn._rRH === RH) return sn._rows
            const out = []
            for (let k = 0; k < 2; k++) {
              for (const [px, sz] of (k === 0 ? sn.b : sn.a)) out.push(rowOf(px), sz, k)
            }
            sn._rows = out; sn._rk = latticeKey; sn._rRH = RH
            return out
          }
          const cbFold = (sn) => {
            const t = cbRowsOf(sn)
            for (let q = 0; q < t.length; q += 3) {
              const r = t[q], sz = t[q + 1]
              const prev = _cbRows.get(r)
              if (prev == null || sz > prev) { _cbRows.set(r, sz); _cbSide.set(r, t[q + 2]) }
            }
          }
          let _liveCb = null, _liveCbSide = null
          let sc = 0
          const first2 = cols.colTime(loI) ?? 0   // same ±1 overdraw as the walk
          while (sc < cbRing.length && cbRing[sc].ts < first2) sc++
          const lastCol = lastI
          for (let i = loI; i <= lastCol; i++) {
            const slotStart = cols.colTime(i)
            if (slotStart == null) continue
            const slotEnd = cols.colTime(i + 1) ?? Infinity
            if (sc >= cbRing.length) break
            _cbRows.clear(); _cbSide.clear()
            // The FORMING column only (was "rightmost walked", which seeded
            // the latest snapshot into an old column when panned back, and
            // would now hit the +1 overdraw column).
            const isLiveCol = i === cols.total - 1
            const liveFrom = isLiveCol ? Math.max(slotStart, now - CB_LIVE_MS) : slotStart
            while (sc < cbRing.length && cbRing[sc].ts < slotEnd) {
              const sn = cbRing[sc]
              if (sn.ts >= liveFrom) cbFold(sn)
              sc++
            }
            if (isLiveCol && cbRing.length) {   // forming-column seed
              cbFold(cbRing[cbRing.length - 1])
            }
            if (_cbRows.size === 0) continue
            if (isLiveCol) { _liveCb = new Map(_cbRows); _liveCbSide = new Map(_cbSide) }
            const _x0 = Math.round(cols.xOfCol(i) * dpr) / dpr
            const _xw = Math.round((cols.xOfCol(i) + cols.cw) * dpr) / dpr - _x0
            for (const [r, sz] of _cbRows) {
              const q = cbQ(sz)
              if (q < 0) continue
              pushHeatFill(LUTREF[q], _x0, r * RH, _xw, RH, histBatch)
            }
          }
          // ── CB forward projection — only when the flow doesn't own the
          // extended region (no HL data). 55% alpha marks it as projection;
          // crossed levels (ask below price / bid above) are impossible
          // statements about the present and are skipped.
          const extC = extendRef ? (extendRef.current | 0) : 0
          if (extC > 0 && !bk.flow.length && _liveCb && _liveCb.size && cols.priceRow != null) {
            if (!useGL) ctx.globalAlpha = 0.55
            const projLast = cols.total - 1 + extC
            for (let i = cols.total; i <= projLast; i++) {
              const _pr = Math.round(cols.xOfCol(i) * dpr) / dpr
              if (_pr > w) break
              const _pw2 = Math.round((cols.xOfCol(i) + cols.cw) * dpr) / dpr - _pr
              for (const [r, sz] of _liveCb) {
                const isAsk = _liveCbSide.get(r) === 1
                if (isAsk ? (r > cols.priceRow) : (r < cols.priceRow)) continue
                const q = cbQ(sz)
                if (q < 0) continue
                // bake 0.55 via drawColorBatches(map, 0.55) when GL; 2D keeps globalAlpha
                pushHeatFill(LUTREF[q], _pr, r * RH, _pw2, RH, histBatch055)
              }
            }
            if (!useGL) ctx.globalAlpha = 1
          }
        }
        // ── Fold AFTER paint ── p25/p97 from this walk's samples.
        // Eased toward the new window (snap only on reset/extreme). Stash
        // percentiles on B so a later histDirty can pre-snap with no
        // extra walk. Record the normalizer these pixels were painted
        // with (not the post-fold HN — that belongs to the next frame).
        if (_hnN >= 16 && normRef) {
          const q = _hnBuf.subarray(0, _hnN)
          q.sort()
          const p25 = q[Math.floor(0.25 * (_hnN - 1))]
          const p97 = q[Math.floor(0.97 * (_hnN - 1))]
          B.p25 = p25
          B.p97 = p97
          B.pOk = true
          _foldHN(p25, p97)   // eased (heat-flash fix) — was snap/EMA .25 per pass
        }
        else B.pOk = false
        B.hnLo = nLo; B.hnSpan = nSpan
      } else if (histReplay && B.pOk && normRef) {
        // Replay frame: the walk would have folded the SAME samples (the key
        // pins every input they come from), so apply the same fold step from
        // the stored percentiles — the normalizer evolves exactly as before.
        _foldHN(B.p25, B.p97)   // same eased fold as the walk path
      }
      // History batches go to GL when freshly walked, or replayed on own-GL.
      // Submitted ahead of the flow — the regions are partitioned, so order
      // between them never overlaps a pixel. (2D drew them during the walk.)
      const _histSubmit = useGL && (histDirty || histReplay)
      if (histDirty) B.histKey = histKey

      // clip the walk's fills to the repaint strip — the walk itself stays
      // whole (run state needs the full history pass; it is cheap Map work,
      // the AREA was the cost) but rasterization is bounded by the strip.
      ctx.save()
      ctx.beginPath()
      ctx.rect(_snapLo, -HEAT_OVERDRAW, Math.max(0, _snapHi - _snapLo), bh)
      ctx.clip()
      const flow = bk.flow
      // On a blit frame only the strip is rasterized, so only entries whose
      // segments reach it need walking — plus ONE older entry to carry the
      // run that spans into the strip. Idle/pan drops from the whole deque
      // to ~2 entries; full frames (zoom/ramp/reset) still walk everything,
      // now against the cache above.
      let walkFrom = 0
      // Empty deque (pre-data, or the frame after a gen reset) makes
      // flow.length - 1 === -1 and flow[-1] undefined — it threw every
      // frame until snapshots arrived, then "fixed itself". Nothing to
      // walk means nothing to paint.
      if (!flow.length) {
        // Strip clip closed FIRST, before the submit — drawBatchOr2D's
        // fallback paints on this ctx, and the batch it replays is the
        // history/CB field, which GL has always drawn unclipped. Leaving the
        // flow strip's clip up would scissor every history column away on
        // the one frame that most needs them. Inert for the paths that were
        // already here: GL ignores 2D clips, and flushHeatValues can only
        // have labels when the flow walk ran, which by definition it has not
        // in this branch (empty deque ⇒ valueByRow empty ⇒ first-guard
        // return).
        ctx.restore()
        if (useGL) {
          if (_histSubmit) { drawBatchOr2D(histBatch, 1); drawBatchOr2D(histBatch055, 0.55) }
          drawBatchOr2D(fillBatch, 1)
          drawBatchOr2D(fillBatch055, 0.55)
          fillBatch.clear(); fillBatch055.clear()
          flushHeatValues()
          if (useScene) {
            gl.commitHeatLayer()
          }
        } else {
          flushHeatValues()
        }
        return
      }
      if (canBlit && pxPerMs > 0) {
        const reach = Math.abs(dir > 0 ? stripB - seamX : seamX - stripA)
        const tMin = now - reach / pxPerMs
        walkFrom = flow.length - 1
        while (walkFrom > 0 && (flow[walkFrom].tsA || flow[walkFrom].ts) >= tMin) walkFrom--
      }

      HEAT_PROBE.flowN = flow.length - walkFrom
      for (let i = walkFrom; i < flow.length; i++) {
        const st = flow[i]
        const x0 = Math.min(clipHi, Math.max(clipLo, xOfTs(st.tsA || st.ts)))
        rowsOf(st)   // synchronous — every entry paints the frame the grid changes
        // ── ONE PASS OVER rr, NOT A SCAN OF open PER ENTRY ──────────────
        // This was two loops: scan every open run against a dense row
        // scratch, then walk rr to open the new ones. O(N·|open|) per
        // paint on top of the per-row scratch load rowsOf did for the sole
        // benefit of that scan's _rsGet. With ~100 rows and a full deque
        // that is the 1,684ms the profile put on this function — and the
        // blit was only hiding it by shrinking N, never R.
        //
        // rr already holds exactly the row/total pairs the scan was looking
        // up, so iterating it does the update AND the open in one pass and
        // the scratch stops being needed at all. What the scan uniquely
        // provided was detecting rows that VANISHED — open here, absent
        // from this entry. A generation stamp finds those without a scan:
        // every run left in open that was visited below carries this
        // entry's stamp, so when the counts agree there is provably nothing
        // stale and the fallback is skipped. Book rows rarely disappear
        // outright, so that is the common case.
        //
        // Semantics are unchanged, including the awkward corner: a run
        // whose bucket drops below the floor is flushed and deleted, and
        // the old second loop would then see !open.has(r) and decline to
        // reopen it because q < 0. Same outcome here via the continue.
        const _rr = st.rr, _rt = st.rt, _rl = st.rl
        const _g = ++_runGen
        let _touched = 0
        for (let j = 0; j < _rr.length; j++) {
          const r = _rr[j]
          const tot = _rt[j]
          const q = bucketOfL(_rl[j])
          const run = open.get(r)
          if (run) {
            if (q !== run.q) {
              flushRun(r, run, x0)
              if (q < 0) { open.delete(r); continue }
              run.x0 = x0; run.q = q
            }
            run.tot = tot
            run.g = _g
            _touched++
          } else if (q >= 0) {
            open.set(r, { x0, q, tot, g: _g })
            _touched++
          }
        }
        // Vanished rows only. open.size counts what survived the pass;
        // _touched counts what the pass visited and left in place. Any
        // difference is a run this entry never mentioned.
        if (_touched !== open.size) {
          for (const [r, run] of open) {
            if (run.g === _g) continue
            flushRun(r, run, x0)
            open.delete(r)
          }
        }
      }
      // ── [SeamProbeY] ── compare this layer's screen-y for the SAME price
      // the canvas side published. Δ names the vertical term: rect basis
      // (element/overdraw/parent) vs row math (r/RH). Flag-gated, 1Hz.
      if (typeof window !== 'undefined' && window.__cheddarSeamY
          && localStorage.getItem('cheddar_seam_probe_y') === '1') {
        const P = window.__cheddarSeamY
        if (!paintNow._spyTs || performance.now() - paintNow._spyTs > 1000) {
          paintNow._spyTs = performance.now()
          const rL = rowOf(P.px)
          const lrect = canvasRef.current?.getBoundingClientRect()
          if (lrect) {
            const yL = lrect.top + HEAT_OVERDRAW + rL * RH
            dbg(`[SeamProbeY] Δy=${(yL - P.y).toFixed(2)} | canvas r=${P.r} RH=${P.RH.toFixed(3)} rect=${P.rectTop.toFixed(1)} y=${P.y.toFixed(1)} | layer r=${rL} RH=${RH.toFixed(3)} rect=${lrect.top.toFixed(1)}+OV y=${yL.toFixed(1)} | age=${(performance.now() - P.t).toFixed(0)}ms`)
          }
        }
      }
      // Same rule as xOfTs — the live edge is the one x that moves every
      // frame, so snapping it is exactly what makes the ribbon step.
      const xNow = Math.min(clipHi, Math.max(clipLo,
        useGL ? seamX : Math.round(seamX * dpr) / dpr))
      // Runs still open at the seam are the LIVE edge — never outlined:
      // they are long-lived by definition and the mark is for short
      // warm-up segments only.
      for (const [r, run] of open) flushRun(r, run, xNow)
      ctx.restore()   // closes the strip clip opened at the blit decision

      if (useGL) {
        // Safe to fall back to 2D here: the strip clip was closed on the
        // line above, so a replay is not scissored to the flow band.
        if (_histSubmit) { drawBatchOr2D(histBatch, 1); drawBatchOr2D(histBatch055, 0.55) }
        drawBatchOr2D(fillBatch, 1)
        drawBatchOr2D(fillBatch055, 0.55)
        fillBatch.clear()
        fillBatch055.clear()
        // Labels into FBO before commit (shared scene) or onto 2D (own GL).
        flushHeatValues()
        if (useScene) {
          gl.commitHeatLayer()
        }
      } else {
        flushHeatValues()
      }

      // The normalizer feed is not in THIS block — it is the percentile fold
      // at the end of the history pass above, which is this layer's single
      // write of heatNormRef. (This note used to say the main pass owned it.
      // That pass was ported into this file and cut from FPCC, so the note
      // was inverted, and it pointed a reader straight at the live writer.)
    }

    // Idle loop: divisor pacing verbatim from the trail, but the body is
    // shared with the canvas-driven hook. Skip the tick if a hook paint
    // just landed this frame — one paint per frame, whoever asked first.
    //
    // Vela-style: sleep the clock while interacting. FPCC's force hook owns
    // those frames (paint-when-changed). Re-arming forever was keeping the
    // shared frameDriver awake at 30fps for transform-only no-ops.
    const draw = (ts) => {
      if (interactingRef?.current) {
        paintNow() // early-out: CSS translateY only
        rafRef.current = 0
        return
      }
      // FRAME_LAYER: the shared driver runs input → anim → draw → layers,
      // so by the time this fires the chart has already drawn from the
      // same geometry refs, off the same ts. This loop used to hold its
      // own rAF and could land on a frame the chart had not painted.
      rafRef.current = requestFrame(draw, FRAME_LAYER)
      if (prevTs > 0) {
        const d = ts - prevTs
        if (d >= 3 && d <= 100) emaDelta = emaDelta * 0.95 + d * 0.05
      }
      prevTs = ts
      const N = Math.max(1, Math.round(FRAME_MS / emaDelta))
      frameCount++
      if (frameCount % N !== 0) return
      if (performance.now() - lastPaintTs < 8) return
      paintNow()
    }

    // Canvas-driven sync: FPCC calls this at the tail of every full draw.
    // GATED on geometry delta: full draws fire on every bursty data tick,
    // and repainting the flow on that cadence destroyed the uniform pacing
    // the divisor loop exists for — the field visibly stuttered. The hook's
    // one job is interaction sync (zoom/pan mutate geometry inside rAF
    // callbacks where ordering vs our loop is arbitrary); when geometry is
    // unchanged, the steady 30fps clock owns the flow.
    const hookPaint = (opts) => {
      // force / gl-submit: always paint — covers drag pan AND wheel/motion ease
      // (both schedule footprint draws; heat must sync on every one).
      if (opts && (opts.mode === 'gl-submit' || opts.mode === 'force')) {
        paintNow(opts)
        // Wake idle clock after interact sleep (and keep it armed while live).
        if (!interactingRef?.current) start()
        const st = stateRef.current
        const g = (typeof st.getGeom === 'function') ? st.getGeom() : null
        const seam = (typeof st.getSeamX === 'function') ? st.getSeamX() : 0
        lastGeomKeyRef.current = g ? `${g.rowHeight}|${Number.isFinite(g.pinPx) ? g.pinPx : g.regionPx}|${seam}|${g.latticeId != null ? g.latticeId : (g.latticeKey || '')}` : ''
        return
      }
      // During/after interact freeze: always reach paintNow (transform-only
      // or forced full catch-up). Key gate would skip catch-up when REG
      // already matched via freeze transforms.
      if (interactingRef?.current || wasInteractingRef.current) {
        paintNow()
        if (!interactingRef?.current && !wasInteractingRef.current) {
          start() // interact ended — resume time-blit clock
          const st = stateRef.current
          const g = (typeof st.getGeom === 'function') ? st.getGeom() : null
          const seam = (typeof st.getSeamX === 'function') ? st.getSeamX() : 0
          lastGeomKeyRef.current = g ? `${g.rowHeight}|${Number.isFinite(g.pinPx) ? g.pinPx : g.regionPx}|${seam}|${g.latticeId != null ? g.latticeId : (g.latticeKey || '')}` : ''
        }
        return
      }
      const st = stateRef.current
      const g = (typeof st.getGeom === 'function') ? st.getGeom() : null
      const seam = (typeof st.getSeamX === 'function') ? st.getSeamX() : 0
      const key = g ? `${g.rowHeight}|${Number.isFinite(g.pinPx) ? g.pinPx : g.regionPx}|${seam}|${g.latticeId != null ? g.latticeId : (g.latticeKey || '')}` : ''
      if (key === lastGeomKeyRef.current) return
      lastGeomKeyRef.current = key
      paintNow()
    }
    if (paintHookRef) paintHookRef.current = hookPaint

    const start = () => { if (!rafRef.current) rafRef.current = requestFrame(draw, FRAME_LAYER) }
    const stop = () => { if (rafRef.current) { cancelFrame(rafRef.current); rafRef.current = 0 } }
    const onVisibility = () => { if (document.hidden) stop(); else start() }
    document.addEventListener('visibilitychange', onVisibility)
    if (!document.hidden) start()
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (paintHookRef && paintHookRef.current === hookPaint) paintHookRef.current = null
      stop()
    }
  }, [active])

  // ── WebGL teardown — UNMOUNT ONLY ─────────────────────────────────────
  // The context created inside paintNow (createFootprintCellGL on
  // glCanvasRef) otherwise outlives this component. The mount is
  // conditional in FPCC —
  //   {(showBookHeat || showCbHeat) && dataSource === 'hyperliquid' && (…)}
  // — so the layer unmounts on every heat toggle and every
  // solana↔hyperliquid switch, and each remount calls createFootprintCellGL
  // on a fresh canvas. Orphaned contexts are not reliably reclaimed by GC;
  // browsers cap the number of live ones and start evicting the oldest.
  // Once creation returns null, glRef.current becomes false — and `=== null`
  // is the "not tried" sentinel, so it never retries. The layer then drops
  // silently to 2D for the rest of that mount — which, now that the band is
  // fully repainted every frame, means a ~250× larger rasterised area on a
  // backend that cannot absorb it. Nothing is logged when it happens.
  //
  // DEPS ARE [] DELIBERATELY — this must NOT ride the [active] effect's
  // cleanup above. `active` is showBookHeat-only while the mount condition
  // also accepts showCbHeat, so active can go false with the component still
  // mounted (CB heat on, book heat off). Releasing there would leave
  // glRef.current holding a DEAD controller: not null, so the "not tried"
  // check never rebuilds it, and every later paint would submit batches into
  // a lost context. Unmount is the only moment the context is truly done.
  //
  // loseContext is the whole teardown — footprintCellGL exposes no dispose,
  // and losing the context drops every program, buffer and texture it
  // allocated in one go. getContext() is the controller's own accessor and
  // already returns null once its dead flag is set, so a context that was
  // lost before unmount costs nothing here. Its webglcontextlost listener
  // will fire from this call and re-set that flag; harmless, the canvas is
  // detached by then and the listener goes with it. Guarded on
  // glRef.current so a mount that never made a context (kill switch off, or
  // create failed) touches nothing.
  useEffect(() => () => {
    const ctl = glRef.current
    glRef.current = null
    if (!ctl) return
    try {
      ctl.getContext?.()?.getExtension('WEBGL_lose_context')?.loseContext()
    } catch {}
  }, [])

  return (
    <div
      className="liquidity-heat-layer"
      style={{
        // Same box discipline as OrderflowTrail: inside the scroll
        // container, clipped by its overflow at the y-axis boundary.
        position: 'absolute',
        top: 0,
        left: 0,
        width,
        height,
        direction: 'ltr',          // escape the wrapper's RTL scaffold
        pointerEvents: 'none',
        zIndex: -1,                // under vol-profile (z0), trail (z1), footprint (z2+)
        overflow: 'hidden',
      }}
    >
      {/* height is set by the paint's sizing block (viewport + 2×overdraw);
          top offsets by the margin so row y=0 still lands at the wrapper's
          top edge. translateY(REG) then moves within pre-drawn content.
          GL underlay = heat fills; 2D on top = warm outlines (or full 2D if GL off). */}
      {/* Own heat GL canvas only when no shared scene; 2D outlines always. */}
      {!sceneGlRef && (
        <canvas ref={glCanvasRef} style={{ display: 'block', position: 'absolute', left: 0, top: `-${HEAT_OVERDRAW}px`, width: '100%', pointerEvents: 'none', willChange: 'transform', zIndex: 0 }} />
      )}
      <canvas ref={canvasRef} style={{ display: 'block', position: 'absolute', left: 0, top: `-${HEAT_OVERDRAW}px`, width: '100%', pointerEvents: 'none', willChange: 'transform', zIndex: 1 }} />
    </div>
  )
}